import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n";
import { useGoTo } from "../nav";
import real3 from "../assets/real-3.jpg";
import real4 from "../assets/real-4.jpg";
import real5 from "../assets/real-5.jpg";
import real7 from "../assets/real-7.jpg";
import real8 from "../assets/real-8.jpg";
import real9 from "../assets/real-9.jpg";
import { Flower, Rainbow, Cloud, PhotoCard } from "./decor";

const PHOTOS = [
  { src: real7, alt: "Our playroom — WELCOME banner, cozy tent, and rainbow curtains" },
  { src: real8, alt: "Kids playing on the alphabet rug during circle time" },
  { src: real3, alt: "Bookshelf and calming corner with favorite stories" },
  { src: real5, alt: "Our rainbow affirmation wall — I am kind, I am smart, I am brave" },
  { src: real4, alt: "Circle Time Center with learning posters and toys" },
  { src: real9, alt: "Friends building with blocks under the WELCOME banner and rainbow curtains" },
];

const FRAME_COLORS = ["#F9C6E3", "#CDEBFB", "#D9F3E3", "#E6DEFA", "#FDE4D0", "#FCF0C8"];

export default function Gallery() {
  const { s } = useLang();
  const goTo = useGoTo();
  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-20 sm:py-24 overflow-hidden">
      <Cloud className="absolute left-[5%] top-10 w-24 opacity-80 animate-drift" />
      <Rainbow className="absolute right-[5%] top-12 w-24 opacity-80 animate-floaty" />
      <Flower className="absolute left-[10%] bottom-10 w-9 animate-sway" petal="#F58FBE" />
      <Flower className="absolute right-[10%] bottom-14 w-7" petal="#8CE0C5" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="font-display text-sm font-semibold tracking-wide text-forest/60 uppercase">
            {s.gallery.eyebrow}
          </span>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl mt-3">
            <span className="text-sageBlue">{s.gallery.h1}</span>
            <span className="text-coral">{s.gallery.h2}</span>
          </h2>
          <p className="mt-4 text-forest/75 text-base sm:text-lg">{s.gallery.sub}</p>
        </div>

        <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 items-start">
          {PHOTOS.map((p, i) => (
            <motion.div
              key={p.src}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
              className="group"
            >
              <PhotoCard
                src={p.src}
                alt={p.alt}
                testid={`gallery-photo-${i + 1}`}
                color={FRAME_COLORS[i % FRAME_COLORS.length]}
                imgClassName="aspect-square object-cover"
                className="w-full transition-transform duration-500 group-hover:scale-[1.02]"
              />
            </motion.div>
          ))}

          {/* CTA tile */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.45, delay: 0.16 }}
            data-testid="gallery-cta-tile"
            className="col-span-2 lg:col-span-3 rounded-3xl bg-pPink shadow-soft flex flex-col items-center justify-center text-center p-8 sm:p-10"
          >
            <Rainbow className="w-16 opacity-90" />
            <p className="font-display font-semibold text-forest text-lg mt-2">Want to see it in person?</p>
            <a
              data-testid="gallery-cta-button"
              href="/contact"
              onClick={(e) => {
                e.preventDefault();
                goTo("/contact");
              }}
              className="mt-3 inline-flex rounded-full bg-darksage text-white font-display font-medium px-6 py-2.5 hover:bg-[#4a338f] transition"
            >
              Schedule a Tour
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
